import { useCallback } from 'react'
import { BaseErrorResponse } from 'types/beneficiaries'
import {
  AmplitudeEventAction,
  AmplitudeEventCategory,
  AmplitudeEventType,
} from 'utils/amplitude/amplitudeEvents'
import track from 'utils/tracker'
import {
  cancelEventMap,
  errorEventMap,
  formEventMap,
  pageEventMap,
  successEventMap,
} from '../constants/eventMap'

export interface EarnTrackingContext {
  currency?: string
  amount?: string
  walletId?: string
  walletName?: string
  portfolioId?: string
  productId?: string
  tab?: string
  action?: string
}

export interface EarnTrackingConfig {
  category?: string
  action?: (typeof AmplitudeEventAction)[keyof typeof AmplitudeEventAction]
  context?: EarnTrackingContext
}

export type EarnTrackingOperation = 'deposit' | 'createPortfolio' | 'withdraw'
export type EarnTrackingFormType = 'deposit' | 'withdraw'
export type EarnTrackingPage = 'home' | 'details'

export const useEarnTracking = () => {
  const trackPageView = useCallback((page: EarnTrackingPage, context?: EarnTrackingContext) => {
    track.Amp.track(pageEventMap[page], {
      category: AmplitudeEventCategory.MERCHANT_PORTAL,
      action: AmplitudeEventAction.VIEW,
      ...context,
    })
  }, [])

  const trackFormInitiate = useCallback(
    (formType: EarnTrackingFormType, context?: EarnTrackingContext) => {
      track.Amp.track(formEventMap[formType], {
        category: AmplitudeEventCategory.MERCHANT_PORTAL,
        action: AmplitudeEventAction.VIEW,
        ...context,
      })
    },
    []
  )

  const trackFormCancel = useCallback(
    (formType: EarnTrackingFormType, context?: EarnTrackingContext) => {
      track.Amp.track(cancelEventMap[formType], {
        category: AmplitudeEventCategory.MERCHANT_PORTAL,
        action: AmplitudeEventAction.CLICK,
        ...context,
      })
    },
    []
  )

  const trackSuccess = useCallback(
    (operation: EarnTrackingOperation, context?: EarnTrackingContext) => {
      track.Amp.track(successEventMap[operation], {
        category: AmplitudeEventCategory.MERCHANT_PORTAL,
        ...context,
      })
    },
    []
  )

  const trackError = useCallback(
    (
      operation: EarnTrackingOperation,
      error: BaseErrorResponse | Error,
      context?: EarnTrackingContext
    ) => {
      track.Amp.track(errorEventMap[operation], {
        category: AmplitudeEventCategory.MERCHANT_PORTAL,
        action: AmplitudeEventAction.ERROR,
        error,
        ...context,
      })
    },
    []
  )

  const trackClick = useCallback(
    (eventName: AmplitudeEventType, context?: EarnTrackingContext, config?: EarnTrackingConfig) => {
      track.Amp.track(eventName, {
        category: config?.category || AmplitudeEventCategory.MERCHANT_PORTAL,
        action: config?.action || AmplitudeEventAction.CLICK,
        ...config?.context,
        ...context,
      })
    },
    []
  )

  return {
    trackPageView,
    trackFormInitiate,
    trackFormCancel,
    trackSuccess,
    trackError,
    trackClick,
  }
}

export default useEarnTracking
